import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { Button } from '@mui/material'
import SonarButton from '@SonarCX/components/atoms/Button2'
import BaseModal from '@SonarCX/components/molecules/ChatModals/BaseModal'
import Setting from '../../Tasks/TableSettings/TableSettingsModal/Setting'

import {
  isUUID,
  nullExpandArray,
  shiftValue,
  uniqueArray
} from '@SonarCX/utilities'
import {
  ALL_COLUMNS,
  FIELD_INCLUDE_LIMIT,
  selectContactsTable,
  setOrder as setSliceOrder,
  setVisible as setSliceVisible
} from '@SonarCX/slices/contactsTable'
import { selectCustomFields } from '@SonarCX/slices/customFields'
import useStyles from './styles'

export const DD_ITEM = 'ORDER'

type TableSettingsModalProps = {
  open: boolean
  onClose: CallableFunction
}

const TableSettingsModal: React.FC<TableSettingsModalProps> = ({
  open,
  onClose
}) => {
  const dispatch = useAppDispatch()

  const { customFields } = useAppSelector(selectCustomFields)
  const { order: savedOrder, visible: savedVisible } =
    useAppSelector(selectContactsTable)

  const [order, setOrder] = React.useState(savedOrder)
  const [visible, setVisible] = React.useState(savedVisible)

  const customersExternalFields = React.useMemo(
    () =>
      customFields
        .filter(
          x =>
            x.object_type === 'external-user' &&
            x.field_type !== 'table' &&
            x.field_type !== 'flow'
        )
        .reduce((acc, curr) => {
          const { id, title } = curr
          acc[id] = title
          return acc
        }, {} as Record<string, string>),
    [customFields]
  )

  const classes = useStyles()

  const cancel = React.useCallback(() => {
    onClose()
  }, [onClose])

  const handleSubmit = React.useCallback(() => {
    dispatch(setSliceOrder(order))
    dispatch(setSliceVisible(visible))
    onClose()
  }, [visible, order, dispatch, onClose])

  const toggleVisible = React.useCallback(
    (key: string) => {
      const storageKey = 'CUSTOMER_TABLE_COLUMN_LAST_ORDER'
      const storedOrder: Record<string, number> = JSON.parse(
        localStorage.getItem(storageKey) ?? '{}'
      )

      if (visible.includes(key)) {
        setVisible(old => {
          return old.filter(id => id !== key)
        })

        setOrder(old => {
          // Save current position of this item
          const idx = old.findIndex(id => id === key)
          storedOrder[key] = idx
          localStorage.setItem(storageKey, JSON.stringify(storedOrder))

          // Remove from order
          return old.filter(id => id !== key)
        })

        return
      }

      setVisible(old => {
        return uniqueArray([...old, key])
      })

      setOrder(old => {
        // Find old index of this key, or put at end
        const idx = storedOrder[key] ?? order.length
        const copy = [...old]
        copy.splice(idx, 0, key)
        return uniqueArray(copy)
      })
    },
    [order, visible]
  )

  const handleDrop = React.useCallback((start, end, onTop) => {
    setOrder(old => {
      // Add filler values to simplify movement logic
      const expanded = nullExpandArray(old)

      // Calculate indices
      const oldIdx = expanded.indexOf(start)
      let newIdx = expanded.indexOf(end)
      newIdx += onTop ? -1 : 1

      // Do movement
      const changed = shiftValue(expanded, oldIdx, newIdx)
      return changed.filter(Boolean)
    })
  }, [])

  const orderSort = React.useCallback(
    ([a, _a], [b, _b]) => {
      const aIndex = order.indexOf(a)
      const bIndex = order.indexOf(b)

      return aIndex - bIndex
    },
    [order]
  )

  const sortedOptions = React.useMemo(() => {
    const entries = Object.entries({
      ...ALL_COLUMNS,
      ...customersExternalFields
    })
    const visibleEntries = entries.filter(([key]) => visible.includes(key))
    return visibleEntries.sort(orderSort)
  }, [customersExternalFields, visible, orderSort])

  const hiddenOptions = React.useMemo(() => {
    const entries = Object.entries({
      ...ALL_COLUMNS,
      ...customersExternalFields
    })
    const hiddenEntries = entries.filter(([key]) => !visible.includes(key))
    return hiddenEntries.sort(orderSort)
  }, [visible, orderSort, customersExternalFields])

  const checkIsCustom = React.useCallback(
    (key: string) => {
      if (!isUUID(key)) {
        return false
      }

      const field = customFields.find(field => field.id === key)
      return Boolean(field)
    },
    [customFields]
  )

  const customLimitReached = React.useMemo(
    () =>
      sortedOptions.filter(opt => checkIsCustom(opt[0])).length >=
      FIELD_INCLUDE_LIMIT,
    [sortedOptions, checkIsCustom]
  )

  return (
    //@ts-ignore
    <BaseModal
      title='Contacts Column Settings'
      {...{ open, onClose }}
      PaperProps={{
        style: {
          minWidth: 300
        }
      }}
      footerActions={
        <div className={classes.controls}>
          <Button onClick={cancel} className={classes.button}>
            CANCEL
          </Button>

          <SonarButton onClick={handleSubmit} className={classes.button}>
            SAVE
          </SonarButton>
        </div>
      }
    >
      <div className={classes.root}>
        {sortedOptions.map(([key, title]) => (
          <Setting
            key={key}
            id={key}
            onClick={() => toggleVisible(key)}
            onDrop={handleDrop}
            isCustom={checkIsCustom(key)}
            required={key === 'name'}
          >
            {title}
          </Setting>
        ))}

        {hiddenOptions.length > 0 && (
          <div className={classes.hidden}>HIDDEN</div>
        )}

        {hiddenOptions.map(([key, title]) => (
          <Setting
            key={key}
            onClick={() => toggleVisible(key)}
            isCustom={checkIsCustom(key)}
            atLimit={customLimitReached}
            atLimitMessage={`Only ${FIELD_INCLUDE_LIMIT} custom fields can be shown at once`}
            hidden
          >
            {title}
          </Setting>
        ))}
      </div>
    </BaseModal>
  )
}

export default TableSettingsModal
